/**
 * hero-slider.js — Hero Slider stile CUPRA
 * Custom element vanilla JS, nessuna dipendenza esterna.
 * Registra <hero-slider> con connectedCallback (stesso pattern del tema).
 */

if (!customElements.get('hero-slider')) {
  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this._slides       = Array.from(this.querySelectorAll('.hero-slider__slide'));
      this._thumbs       = Array.from(this.querySelectorAll('.hero-slider__thumb'));
      this._activeIndex  = 0;
      this._timer        = null;
      this._observer     = null;
      this._inViewport   = false;
      this._paused       = false;  // pausa hover/focus

      // Applica velocità transizione dal dataset
      const speed = parseInt(this.dataset.transitionSpeed, 10) || 700;
      this.style.setProperty('--hs-transition', `${speed}ms`);

      this._bindThumbs();
      this._bindHoverPause();
      this._bindDrag();
      this._setupObserver();

      // Avvia autoplay solo se impostato (l'observer lo attiverà al primo inViewport)
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._observer) this._observer.disconnect();
    }

    // ── Vai allo slide N ──────────────────────────────────────────────────
    _goTo(index) {
      const count = this._slides.length;
      if (count === 0) return;
      index = ((index % count) + count) % count;

      // Rimuovi active dal precedente
      const prev = this._slides[this._activeIndex];
      const prevThumb = this._thumbs[this._activeIndex];
      if (prev) prev.classList.remove('is-active');
      if (prevThumb) {
        prevThumb.classList.remove('is-active');
        prevThumb.setAttribute('aria-selected', 'false');
      }

      this._activeIndex = index;

      // Attiva il nuovo
      const next = this._slides[this._activeIndex];
      const nextThumb = this._thumbs[this._activeIndex];
      if (next) next.classList.add('is-active');
      if (nextThumb) {
        nextThumb.classList.add('is-active');
        nextThumb.setAttribute('aria-selected', 'true');
        this._scrollThumbIntoView(nextThumb);
      }
    }

    // ── Scrolla la thumbnail attiva nella barra (mobile overflow) ─────────
    _scrollThumbIntoView(thumb) {
      const bar = thumb.closest('.hero-slider__thumb-bar');
      if (!bar) return;
      const target = thumb.offsetLeft - bar.offsetWidth / 2 + thumb.offsetWidth / 2;
      bar.scrollTo({ left: target, behavior: 'smooth' });
    }

    // ── Click sulle thumbnail ─────────────────────────────────────────────
    _bindThumbs() {
      this._thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.dataset.thumbIndex, 10);
          this._goTo(idx);
          this._restartAutoplay();
        });
      });
    }

    // ── Pausa su hover e focus per accessibilità ──────────────────────────
    _bindHoverPause() {
      this.addEventListener('mouseenter', () => {
        this._paused = true;
        this._stopAutoplay();
      });
      this.addEventListener('mouseleave', () => {
        this._paused = false;
        if (this._inViewport && this.dataset.autoplay === 'true') {
          this._startAutoplay();
        }
      });
      this.addEventListener('focusin', () => {
        this._paused = true;
        this._stopAutoplay();
      });
      this.addEventListener('focusout', () => {
        // Aspetta un tick: se focus rimane dentro non ripartire
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) {
            this._paused = false;
            if (this._inViewport && this.dataset.autoplay === 'true') {
              this._startAutoplay();
            }
          }
        });
      });
    }

    // ── Autoplay ──────────────────────────────────────────────────────────
    _startAutoplay() {
      if (this._slides.length < 2) return;
      if (this._paused || !this._inViewport) return;
      this._stopAutoplay();
      const interval = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
      this._timer = setInterval(() => this._goTo(this._activeIndex + 1), interval);
    }

    _stopAutoplay() {
      clearInterval(this._timer);
      this._timer = null;
    }

    _restartAutoplay() {
      if (this.dataset.autoplay === 'true') {
        this._stopAutoplay();
        this._startAutoplay();
      }
    }

    // ── Drag / swipe (mouse + touch + stylus) ────────────────────────────
    _bindDrag() {
      const THRESHOLD = 50;
      let startX = 0;
      let isDragging = false;

      // Il browser gestisce scroll verticale nativo;
      // i movimenti orizzontali arrivano al JS senza essere intercettati.
      this.style.touchAction = 'pan-y';

      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // Se il click è su una thumbnail lascia passare il click handler esistente
        if (e.target.closest('.hero-slider__thumb')) return;
        startX = e.clientX;
        isDragging = true;
        // Capture solo per touch/stylus; per il mouse il capture rompe i click
        if (e.pointerType !== 'mouse') this.setPointerCapture(e.pointerId);
        this._stopAutoplay();
      });

      this.addEventListener('pointerup', (e) => {
        if (!isDragging) return;
        isDragging = false;
        const delta = e.clientX - startX;
        if (Math.abs(delta) >= THRESHOLD) {
          delta < 0
            ? this._goTo(this._activeIndex + 1)
            : this._goTo(this._activeIndex - 1);
        }
        this._restartAutoplay();
      });

      this.addEventListener('pointercancel', () => {
        isDragging = false;
        this._restartAutoplay();
      });
    }

    // ── IntersectionObserver: pausa fuori viewport ────────────────────────
    _setupObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: considera sempre in viewport
        this._inViewport = true;
        if (this.dataset.autoplay === 'true') this._startAutoplay();
        return;
      }
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this._inViewport = entry.isIntersecting;
            if (this._inViewport && !this._paused && this.dataset.autoplay === 'true') {
              this._startAutoplay();
            } else {
              this._stopAutoplay();
            }
          });
        },
        { threshold: 0.2 }
      );
      this._observer.observe(this);
    }
  }

  customElements.define('hero-slider', HeroSlider);
}
